import theme from '@modules/theme';
import { Dialog } from '@mui/material';
import { Team } from '@type/db-types';
import { CSVImporter } from 'csv-import-react';
import { Data, UserImport } from './ImportModal';
import { groupBy, map, uniqBy } from 'lodash';
import { createTeam } from '@modules/supabase/teams';
import { getSpoofUrl } from '@modules/api';
import { getUserByEmail } from '@modules/supabase/users';
import invariant from 'tiny-invariant';
import exportFromJSON from 'export-from-json';
import { handleTeamMemberImport, TEAM_MEMBER_COLUMNS } from '@utils/import-utils';

interface TeamImport extends UserImport {
    teamName: string;
    fee: number;
    codeOfConduct: string;
    coachEmail: string;
    coachFirstName: string;
    coachLastName: string;
}

interface Props {
    onClose: () => void;
    // showModal: boolean;
}

export default function TeamImportModal({ onClose }: Props) {
    return (
        <Dialog fullWidth maxWidth="lg" open={true} onClose={onClose}>
            <CSVImporter
                isModal={false}
                darkMode={true}
                // waitOnComplete
                primaryColor={theme.palette.primary.main}
                onComplete={async (data: Data<TeamImport>) => {
                    const allRows = data.rows.map(({ values }) => values);
                    // validate data
                    if (dataIsValid(allRows)) {
                        console.log('allRows', allRows);
                        // create teams
                        const teams: (Team & { spoofUrl: string })[] = await Promise.all(
                            uniqBy(allRows, 'teamName').map(
                                async ({
                                    coachEmail,
                                    coachFirstName,
                                    coachLastName,
                                    teamName,
                                    codeOfConduct,
                                    fee,
                                }) => {
                                    const spoofUrl = await getSpoofUrl({
                                        email: coachEmail,
                                        firstName: coachFirstName,
                                        lastName: coachLastName,
                                        existingUser: false,
                                    });
                                    invariant(spoofUrl, 'initCoach must be defined');
                                    const coach = await getUserByEmail(coachEmail);
                                    invariant(coach, 'coach must be created');
                                    const team = await createTeam(
                                        {
                                            name: teamName,
                                            codeOfConduct: codeOfConduct || '',
                                            fee,
                                            stripeDetailsSubmitted: false,
                                            imageUrl: '',
                                            payToUserId: coach.id,
                                            createdByUserId: coach.id,
                                        },
                                        'import',
                                    );
                                    invariant(team, 'team must be created successfully');
                                    return { spoofUrl, ...team };
                                },
                            ),
                        );

                        alert(`created ${teams.length} teams`);
                        console.log('created teams', teams);

                        // create team members
                        const users = await Promise.all(
                            allRows.map((data) => {
                                const team = teams.find(({ name }) => name === data.teamName);
                                invariant(team, 'team must be found');
                                handleTeamMemberImport(data, team.id as string);
                            }),
                        );

                        alert(`created ${users.length} users`);
                        exportFromJSON({
                            data: teams,
                            fileName: `imported teams_${Date.now()}`,
                            exportType: exportFromJSON.types.csv,
                        });
                        onClose();
                    } else {
                        alert('Data is not valid');
                        onClose();
                    }
                }}
                template={{
                    columns: [
                        ...TEAM_MEMBER_COLUMNS,
                        {
                            name: 'Coach Email',
                            key: 'coachEmail',
                            required: true,
                            description: 'email',
                            suggested_mappings: ['coach email'],
                        },
                        {
                            name: 'Coach First Name',
                            key: 'coachFirstName',
                            required: true,
                            description: 'last name',
                            suggested_mappings: ['Coach First Name', 'coach Name', 'coach'],
                        },
                        {
                            name: 'Coach Last Name',
                            key: 'coachLastName',
                            required: true,
                            description: 'last name',
                            suggested_mappings: ['coach last Name', 'coach last'],
                        },
                        {
                            name: 'Team Name',
                            key: 'teamName',
                            required: true,
                            description: 'teams name',
                            suggested_mappings: ['team', 'Team Name'],
                        },
                        {
                            name: 'Joining Fee',
                            key: 'fee',
                            required: true,
                            description: 'teams fee',
                            suggested_mappings: ['joiningFee', 'cost', 'price', 'fee'],
                        },
                        {
                            name: 'Code of Conduct',
                            key: 'codeOfConduct',
                            required: true,
                            description: 'Code of Conduct',
                            suggested_mappings: ['Code of Conduct', 'conduct'],
                        },
                    ],
                }}
            />
        </Dialog>
    );
}

const dataIsValid = (data: TeamImport[]): boolean => {
    const groupedByTeam = groupBy(data, 'teamName');
    return (
        data.every((row, i) => {
            const {
                firstName,
                lastName,
                weight,
                birthday,
                coachEmail,
                teamName,
                fee,
                // codeOfConduct, // not available
                coachFirstName,
                coachLastName,
                // gender, not always available
            } = row;
            if (!firstName) {
                alert(`Row ${i} is missing a first name`);
                return false;
            }
            if (!lastName) {
                alert(`Row ${i} is missing a last name`);
                return false;
            }
            if (!weight) {
                alert(`Row ${i} is missing a weight`);
                return false;
            }
            if (!birthday) {
                alert(`Row ${i} is missing a birthday`);
                return false;
            }
            if (!coachEmail) {
                alert(`Row ${i} is missing a coach email`);
                return false;
            }
            if (!teamName) {
                alert(`Row ${i} is missing a team name`);
                return false;
            }
            if (!fee) {
                alert(`Row ${i} is missing a fee`);
                return false;
            }
            // if (!codeOfConduct) {
            //     alert(`Row ${i} is missing a code of conduct`);
            //     return false;
            // }
            if (!coachFirstName) {
                alert(`Row ${i} is missing a coach first name`);
                return false;
            }
            if (!coachLastName) {
                alert(`Row ${i} is missing a coach last name`);
                return false;
            }
            return true;
        }) &&
        map(groupedByTeam, (members: TeamImport[]) => {
            const {
                teamName,
                coachEmail: cEmail,
                coachFirstName: cFirstName,
                coachLastName: cLastName,
            } = members[0];
            return members.every(({ coachEmail, coachFirstName, coachLastName }, i) => {
                if (!coachEmail) {
                    alert(`${teamName}: Row ${i} is missing a coach email`);
                    return false;
                }
                if (!coachFirstName) {
                    alert(`${teamName}: Row ${i} is missing a coach first name`);
                    return false;
                }
                if (!coachLastName) {
                    alert(`${teamName}: Row ${i} is missing a coach last name`);
                    return false;
                }
                if (coachEmail !== cEmail) {
                    alert(`${teamName}: Row ${i} has a different coach email`);
                    return false;
                }
                if (coachFirstName !== cFirstName) {
                    alert(`${teamName}: Row ${i} has a different coach first name`);
                    return false;
                }
                if (coachLastName !== cLastName) {
                    alert(`${teamName}: Row ${i} has a different coach last name`);
                    return false;
                }
                return true;
            });
        }).every((x) => x)
    );
};
